/** Formatting helpers. Instants arrive as UTC ISO strings; display uses the project zone. */

export function num(value: number | null | undefined, digits = 2, suffix = ""): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${value.toLocaleString("en-GB", { minimumFractionDigits: digits, maximumFractionDigits: digits })}${suffix}`;
}

export function pct(value: number | null | undefined, digits = 2): string {
  return num(value, digits, "%");
}

export function mwh(value: number | null | undefined, digits = 2): string {
  return num(value, digits, " MWh");
}

/** "11 Aug 2026, 12:00 CEST" for a UTC instant, in the project zone. */
export function dateTime(iso: string | null | undefined, timeZone: string, withZone = true): string {
  if (!iso) return "—";
  const date = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZoneName: withZone ? "short" : undefined,
  }).formatToParts(date);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
  const zone = withZone ? ` ${get("timeZoneName")}` : "";
  return `${get("day")} ${get("month")} ${get("year")}, ${get("hour")}:${get("minute")}${zone}`;
}

/** A naive project-local ISO string ("2026-08-11T12:00:00") shown as written. */
export function localText(naive: string | null | undefined): string {
  if (!naive) return "—";
  const [date, time = "00:00"] = naive.split("T");
  const [year, month, day] = date.split("-").map(Number);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${String(day).padStart(2, "0")} ${months[month - 1]} ${year}, ${time.slice(0, 5)}`;
}

/** "11 Aug 2026" for a calendar date (YYYY-MM-DD). */
export function day(isoDate: string | null | undefined): string {
  if (!isoDate) return "—";
  return localText(`${isoDate.slice(0, 10)}T00:00`).split(",")[0];
}

export function yearLabel(year: number, compact = false): string {
  if (year === 0) return compact ? "Commissioning" : "Commissioning test (Year 0)";
  return `Year ${year}`;
}

export function statusClass(status: string | null | undefined): "pass" | "review" | "miss" | "pending" {
  if (!status) return "pending";
  const upper = status.toUpperCase();
  if (upper === "PASS") return "pass";
  if (upper === "DOES NOT MEET TARGET") return "miss";
  return "review";
}

export function bytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}
